import React, { useState } from 'react';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';

interface Props {
  label: string;
  value?: string | null;
  /** Texto exibido quando não há valor gravado. */
  emptyLabel?: string;
  hint?: string;
}

/**
 * Campo de leitura para um segredo que a tela PODE mostrar.
 *
 * O Client Secret e a senha do certificado são exibidos por decisão explícita:
 * as telas onde este componente aparece já são restritas a quem opera a
 * integração, e essas pessoas precisam dos valores para diagnosticar com curl
 * fora do sistema — era exatamente por não tê-los que a senha gerada na
 * renovação tinha de ser anotada à mão no momento em que aparecia.
 *
 * Fica oculto até alguém clicar. Não é uma camada de segurança — o valor já
 * veio na resposta da API — é para que abrir o cadastro de uma conta com
 * alguém ao lado não signifique expor a credencial sem querer.
 */
export const SecretField: React.FC<Props> = ({ label, value, emptyLabel, hint }) => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const has = Boolean(value && value.length > 0);

  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Área de transferência bloqueada (contexto inseguro, permissão negada):
      // revelar o valor deixa o operador copiar à mão, então não vale um erro.
      setVisible(true);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <span
          className={`flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm break-all ${
            has ? 'font-mono text-slate-800' : 'text-slate-400'
          }`}
        >
          {!has ? emptyLabel ?? 'Não informado' : visible ? value : '•'.repeat(Math.min(value!.length, 32))}
        </span>
        {has && (
          <>
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              title={visible ? 'Ocultar' : 'Mostrar'}
              aria-label={visible ? `Ocultar ${label}` : `Mostrar ${label}`}
            >
              {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            <button
              type="button"
              onClick={copy}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              title="Copiar"
              aria-label={`Copiar ${label}`}
            >
              {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
            </button>
          </>
        )}
      </div>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
};
